import React, { useMemo, useState } from 'react';
import {
  getConvertedToClientDate,
  lastLeadStageFromHistory,
  toDateSafe,
  getProgramTier,
} from '../../utils/accountLifecycle';
import { SIGNING_CHANCE_META } from '../../utils/leadSigningChance';

/**
 * Read-only view of everything a client doc still carries from its lead days.
 *
 * Conversion happens in place on the same account document, so the intake / discovery /
 * analysis summaries, the contact log, the substatus timeline and the pipeline history are all
 * still here — they were just hidden once `type` flipped to Clients. `leadSnapshot` (written at
 * conversion since Oct 2026) freezes the stage/substatus that `stage` overwrote with the tier;
 * older conversions fall back to the "Converted to Client" statusHistory entry.
 */

/** Glass surface shared with the page shell (ClientLeadHistory). */
export const LEAD_HISTORY_GLASS =
  'bg-white/60 dark:bg-gray-900/50 backdrop-blur-xl border border-white/70 dark:border-white/10 shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.35)]';

export const leadHistoryStyles = `
  @keyframes lh-rise {
    from { opacity: 0; transform: translateY(12px) scale(0.99); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .lh-rise { animation: lh-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both; }
  @media (prefers-reduced-motion: reduce) {
    .lh-rise { animation: none; }
  }
`;

const GLASS = LEAD_HISTORY_GLASS;

// Spelled out so Tailwind's JIT keeps every class.
const TINTS = {
  red: 'bg-red-50 ring-red-100 dark:bg-red-500/10 dark:ring-red-400/20',
  green: 'bg-green-50 ring-green-100 dark:bg-green-500/10 dark:ring-green-400/20',
  blue: 'bg-blue-50 ring-blue-100 dark:bg-blue-500/10 dark:ring-blue-400/20',
  amber: 'bg-amber-50 ring-amber-100 dark:bg-amber-500/10 dark:ring-amber-400/20',
  purple: 'bg-purple-50 ring-purple-100 dark:bg-purple-500/10 dark:ring-purple-400/20',
  gray: 'bg-gray-100 ring-gray-200 dark:bg-white/5 dark:ring-white/10',
};

const fmtDate = (value) => {
  const d = toDateSafe(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
};

const fmtDateTime = (d) =>
  d
    ? `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })}`
    : '—';

/** Emoji for a pipeline (statusHistory) entry, picked from its action / stage wording. */
function pipelineEmoji(h) {
  const a = `${h?.action || ''} ${h?.status || ''}`.toLowerCase();
  if (/converted to client/.test(a)) return '🎉';
  if (/\blost\b/.test(a)) return '❌';
  if (/invite/.test(a)) return '✉️';
  if (/signed up/.test(a)) return '🙋';
  if (/proposal/.test(a)) return '📄';
  if (/assessment/.test(a)) return '📋';
  if (/discovery|analysis|call|meeting/.test(a)) return '📞';
  if (/lock/.test(a)) return '🔐';
  if (/received|new lead/.test(a)) return '📥';
  if (h?.substatus) return '🔖';
  return '➡️';
}

const CHANNEL_EMOJI = { email: '✉️', call: '📞', phone: '📞', text: '💬', sms: '💬', meeting: '🎥', video: '🎥', linkedin: '🔗' };
const channelEmoji = (channel) => CHANNEL_EMOJI[String(channel || '').toLowerCase()] || '💬';

/**
 * Note timestamp. Prefer the ISO `timestamp`/`createdAt`: the display `date` field is a short
 * "8/31 2:14PM" string with no year, which parses to the wrong year.
 */
function noteDate(n) {
  const iso = toDateSafe(n.timestamp) || toDateSafe(n.createdAt);
  if (iso) return iso;
  return typeof n.date === 'string' && !/^\d{4}-/.test(n.date) ? null : toDateSafe(n.date);
}

function noteAuthor(n) {
  const a = n.author;
  if (a && typeof a === 'object') return a.name || '';
  return a || n.updatedBy || n.createdBy || '';
}

function StatTile({ emoji, tint = 'gray', label, value, sub, delay = 0, accentDot }) {
  return (
    <div className={`lh-rise ${GLASS} rounded-2xl p-4 flex flex-col items-center text-center gap-1.5`} style={{ animationDelay: `${delay}ms` }}>
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full ring-1 text-xl ${TINTS[tint] || TINTS.gray}`} aria-hidden>
        {emoji}
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`inline-flex items-center gap-1.5 text-sm font-semibold break-words ${value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
        {accentDot ? <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accentDot }} aria-hidden /> : null}
        {value || '—'}
      </span>
      {sub ? <span className="text-[11px] text-gray-400 dark:text-gray-500">{sub}</span> : null}
    </div>
  );
}

function Section({ emoji, title, count, delay = 0, children }) {
  return (
    <section className={`lh-rise ${GLASS} rounded-2xl p-5 md:p-6`} style={{ animationDelay: `${delay}ms` }}>
      <h2 className="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
        <span aria-hidden>{emoji}</span>
        {title}
        {typeof count === 'number' ? (
          <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300">{count}</span>
        ) : null}
      </h2>
      {children}
    </section>
  );
}

function EmptyNote({ emoji, children }) {
  return (
    <p className="py-6 text-sm text-center text-gray-500 dark:text-gray-400">
      <span className="block text-2xl mb-1" aria-hidden>{emoji}</span>
      {children}
    </p>
  );
}

const Chip = ({ children, strong = false }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
      strong
        ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-300'
        : 'border-gray-200 bg-white/70 text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300'
    }`}
  >
    {children}
  </span>
);

const SUMMARY_TABS = [
  { key: 'intake', emoji: '📝', label: 'Intake', pick: (a) => a?.intakeNote || a?.leadSummary },
  { key: 'discovery', emoji: '📞', label: 'Discovery Call', pick: (a) => a?.discoveryCallSummary },
  { key: 'analysis', emoji: '🔍', label: 'Analysis Call', pick: (a) => a?.analysisCallSummary },
];

export default function ClientLeadHistoryTab({ account }) {
  const snapshot = account?.leadSnapshot && typeof account.leadSnapshot === 'object' ? account.leadSnapshot : null;
  const convertedAt = getConvertedToClientDate(account);

  const history = useMemo(() => {
    const all = Array.isArray(account?.statusHistory) ? account.statusHistory : [];
    const convIdx = all.findIndex((h) => /converted to client/i.test(String(h?.action || '')));
    const leadEra = convIdx >= 0 ? all.slice(0, convIdx + 1) : all;
    return [...leadEra].reverse();
  }, [account?.statusHistory]);

  const substatusTimeline = useMemo(() => {
    const src = snapshot?.substatusDates || account?.substatusDates || {};
    return Object.entries(src)
      .map(([name, date]) => ({ name, date: toDateSafe(date) }))
      .filter((r) => r.date)
      .sort((a, b) => a.date - b.date);
  }, [snapshot, account?.substatusDates]);

  const contactNotes = useMemo(() => {
    const notes = Array.isArray(account?.notes) ? account.notes : [];
    return notes
      .filter((n) => n && (n.contact === true || n.contact === 'true'))
      .map((n) => {
        const rawText = n.text || n.note || n.content || n.message || '';
        return {
          date: noteDate(n),
          // "BBA — Email logged" → "Email logged"; the direction is shown as its own badge
          text: String(rawText).replace(/^(BBA|Client)\s+—\s+/i, ''),
          by: noteAuthor(n),
          channel: n.interaction?.channel || n.method || n.channel || '',
          initiatedBy: n.interaction?.initiatedBy || '',
        };
      })
      .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
  }, [account?.notes]);

  const channelCounts = useMemo(() => {
    const counts = {};
    contactNotes.forEach((n) => {
      const key = String(n.channel || 'other').toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [contactNotes]);

  const summaries = SUMMARY_TABS.map((t) => ({ ...t, text: t.pick(account) })).filter((t) => t.text && String(t.text).trim());
  const [summaryKey, setSummaryKey] = useState(null);
  const activeSummary = summaries.find((s) => s.key === summaryKey) || summaries[0];

  const stageBefore = snapshot?.stage || account?.stageBeforeLost || lastLeadStageFromHistory(account);
  const substatus = snapshot?.substatus || null;
  const leadSource = snapshot?.leadSource || account?.leadSource || account?.source || null;
  const signingKey = String(snapshot?.signingChance || account?.signingChance || '').toLowerCase();
  const signingMeta = SIGNING_CHANCE_META[signingKey];
  const received = snapshot?.receivedDate || account?.receivedDate || null;
  const programChosen = snapshot?.program || getProgramTier(account);
  const qualifiedRaw = snapshot?.qualified ?? account?.qualified;
  const qualified = typeof qualifiedRaw === 'boolean' ? (qualifiedRaw ? 'Yes' : 'No') : null;

  const stats = [
    { emoji: '🚩', tint: 'red', label: 'Final lead stage', value: stageBefore, sub: substatus || null },
    {
      emoji: '🎉',
      tint: 'green',
      label: 'Converted',
      value: fmtDate(convertedAt),
      sub: snapshot?.convertedBy ? `by ${snapshot.convertedBy}` : null,
    },
    { emoji: '🎯', tint: 'purple', label: 'Program chosen', value: programChosen },
    { emoji: '📣', tint: 'blue', label: 'Lead source', value: leadSource },
    {
      emoji: '🎲',
      tint: 'amber',
      label: 'Signing chance',
      value: signingMeta?.label || (signingKey ? signingKey : null),
      accentDot: signingMeta?.dotColor,
    },
    { emoji: '📥', tint: 'gray', label: 'Lead received', value: fmtDate(received) },
    { emoji: '✅', tint: 'green', label: 'Qualified', value: qualified },
    { emoji: '💬', tint: 'blue', label: 'Contact touches', value: String(contactNotes.length) },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 mb-6">
        {stats.map((s, i) => (
          <StatTile key={s.label} {...s} delay={60 + i * 35} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6 min-w-0">
          {summaries.length ? (
            <Section emoji="🗂️" title="Lead Summaries" delay={320}>
              {summaries.length > 1 ? (
                <div className="flex justify-center mb-4">
                  <div className="inline-flex flex-wrap justify-center gap-1 p-1 rounded-xl bg-gray-100/80 dark:bg-white/5" role="tablist" aria-label="Lead summaries">
                    {summaries.map((s) => {
                      const on = s.key === activeSummary.key;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          role="tab"
                          aria-selected={on}
                          onClick={() => setSummaryKey(s.key)}
                          className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            on
                              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <span className="mr-1.5" aria-hidden>{s.emoji}</span>
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-xs font-semibold uppercase tracking-wider text-center text-gray-500 dark:text-gray-400 mb-3">
                  {activeSummary.emoji} {activeSummary.label}
                </p>
              )}
              <div className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap max-h-[28rem] overflow-y-auto pr-1">
                {String(activeSummary.text)}
              </div>
            </Section>
          ) : null}

          <Section emoji="🧭" title="Pipeline History" count={history.length} delay={360}>
            {history.length === 0 ? (
              <EmptyNote emoji="🕳️">No lead-era stage changes were recorded.</EmptyNote>
            ) : (
              <ol className="divide-y divide-gray-100 dark:divide-white/5">
                {history.map((h, i) => {
                  const date = toDateSafe(h?.date);
                  const title = h?.action || (h?.substatus ? `Substatus → ${h.substatus}` : h?.status ? `Stage → ${h.status}` : 'Status change');
                  const stageMoved = h?.previousStatus && h?.status && h.previousStatus !== h.status;
                  const subMoved = h?.substatus && h.substatus !== h?.previousSubstatus;
                  return (
                    <li key={`${h?.date || i}-${i}`} className="flex items-start gap-3 py-3">
                      <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gray-100/80 dark:bg-white/5 text-lg" aria-hidden>
                        {pipelineEmoji(h)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{title}</div>
                        {stageMoved || subMoved ? (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-gray-400">
                            {stageMoved ? (
                              <>
                                <Chip>{h.previousStatus}</Chip>
                                <span aria-hidden>→</span>
                                <Chip strong>{h.status}</Chip>
                              </>
                            ) : null}
                            {subMoved ? <Chip>🔖 {h.substatus}</Chip> : null}
                          </div>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDateTime(date)}</div>
                        {h?.updatedBy ? <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{h.updatedBy}</div> : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
        </div>

        <div className="space-y-6 min-w-0">
          {substatusTimeline.length ? (
            <Section emoji="🪜" title="Substatus Timeline" delay={400}>
              <ol className="relative ml-2 border-l-2 border-dashed border-gray-200 dark:border-white/10 space-y-4">
                {substatusTimeline.map((row) => (
                  <li key={`${row.name}-${row.date.getTime()}`} className="relative pl-6">
                    <span className="absolute -left-[0.6875rem] top-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white dark:bg-gray-900 text-xs" aria-hidden>
                      ✅
                    </span>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{row.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{fmtDateTime(row.date)}</div>
                  </li>
                ))}
              </ol>
            </Section>
          ) : null}

          <Section emoji="📇" title="Contact Log" count={contactNotes.length} delay={440}>
            {contactNotes.length === 0 ? (
              <EmptyNote emoji="📭">No contact touches were logged.</EmptyNote>
            ) : (
              <>
                {channelCounts.length > 1 ? (
                  <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                    {channelCounts.map(([channel, n]) => (
                      <Chip key={channel}>
                        {channelEmoji(channel)} <span className="ml-1 capitalize">{channel}</span>
                        <span className="ml-1 font-semibold">{n}</span>
                      </Chip>
                    ))}
                  </div>
                ) : null}
                <ul className="divide-y divide-gray-100 dark:divide-white/5 max-h-[32rem] overflow-y-auto pr-1">
                  {contactNotes.map((n, i) => (
                    <li key={`${n.date?.getTime() || i}-${i}`} className="flex items-start gap-3 py-2.5">
                      <span className="shrink-0 text-lg leading-6" aria-hidden>{channelEmoji(n.channel)}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900 dark:text-white truncate">{n.text || 'Contact logged'}</span>
                          {n.initiatedBy ? (
                            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                              {String(n.initiatedBy).toLowerCase() === 'bba' ? 'Outbound' : 'Inbound'}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {[n.date ? fmtDateTime(n.date) : null, n.by].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
